import type { AdminUserDetail, AdminUserPage, AdminUserRow } from './types';

/**
 * The account surfaces' wire adapters.
 *
 * **`backend-auth` spells the account's role `role_name`; this bundle reads it
 * as `role`.** The two names were never reconciled, so `user.role` was
 * `undefined` on every roster row and on every account detail, and
 * `roleLabel(undefined)` fell through its lookup to render an empty chip —
 * twenty of them on `/users`, one on `/users/:userId`. Nothing threw and nothing
 * logged: an absent key on a cast payload is a hole the type system cannot see.
 *
 * It is mapped here now, once, at the boundary the payload enters through.
 * The same defect took the role dialog with it — the dialog seeds its `<select>`
 * from `user.role` and compares the submitted value against it to decide whether
 * anything changed, so an `undefined` current role made every selection look
 * like a change and the confirmation it opened named a role it could not write.
 *
 * The wire shapes are declared here rather than reusing the exported interfaces:
 * these describe what the service sends, those describe what this bundle reads.
 * Collapsing the two is what let the mismatch exist.
 */

interface WireAdminUserRow {
  id: string;
  display_name: string;
  handle: string;
  phone_e164?: string | null;
  role_name?: string;
  admin_roles?: Record<string, string>;
  is_blocked: boolean;
  avatar_url?: string | null;
  created_at?: string;
}

interface WireAdminUserDetail extends WireAdminUserRow {
  biography?: string;
  role_granted_at?: string | null;
  role_effective_at?: string | null;
}

interface WireAdminUserPage {
  items: WireAdminUserRow[];
  page_size: number;
  total: number;
  has_next: boolean;
}

/**
 * The account default, spelled out.
 *
 * `auth__roles` seeds `user` as the role every account holds until one is
 * granted. A payload missing the key is a payload this bundle cannot read rather
 * than an account with no role, and defaulting keeps the chip populated instead
 * of reintroducing the blank it replaced.
 */
const DEFAULT_ACCOUNT_ROLE = 'user';

export function toAdminUserRow(wire: WireAdminUserRow): AdminUserRow {
  return {
    id: wire.id,
    display_name: wire.display_name,
    handle: wire.handle,
    phone_e164: wire.phone_e164 ?? null,
    role: wire.role_name ?? DEFAULT_ACCOUNT_ROLE,
    admin_roles: wire.admin_roles ?? {},
    is_blocked: wire.is_blocked,
    avatar_url: wire.avatar_url ?? null,
  };
}

export function toAdminUserDetail(wire: WireAdminUserDetail): AdminUserDetail {
  return {
    ...toAdminUserRow(wire),
    ...(wire.biography === undefined ? {} : { biography: wire.biography }),
    role_granted_at: wire.role_granted_at ?? null,
    role_effective_at: wire.role_effective_at ?? null,
  };
}

export function toAdminUserPage(wire: WireAdminUserPage): AdminUserPage {
  return {
    items: wire.items.map(toAdminUserRow),
    page_size: wire.page_size,
    total: wire.total,
    has_next: wire.has_next,
  };
}
